// src/pos-app/surfaces/cashier/CartPanel.js
//
// F12a-N Task 3 — the order-builder cart column of the native Sell surface
// (design contract: docs/mockups/f12a-n/cashier-sell.html, cart + promo
// sheet). Header context, line items with addon pills + per-item notes +
// qty steppers, "N offers available" bar + promo bottom sheet, Discount /
// Notes inline expanders, and the PINNED totals block with Charge + Hold.
// The line-item body is the ONLY scroller (HARD no-scroll rule, spec §6).
//
// Business logic is REUSED, never forked:
// - All order state lives in the parent's useOrderBuilder instance; every
//   mutation goes through its handlers (qty stepper → the stock-guarded
//   handleUpdateProductQuantity, notes → handleUpdateProductNotes, remove
//   → handleRemoveProduct) exactly as the legacy cart does
//   (CashierSalesPage.js:1846-1910).
// - Totals come from utils/OrderManagement.calculateTotals via the
//   builder's memoizedTotals — NEVER recomputed here. The displayed total
//   subtracts the applied promo exactly like legacy 2043.
// - Discount semantics copy legacy 1912-1979 verbatim: three modes
//   (direct / percentage / payable), percentage converts off the live
//   subtotal, payable converts off subtotal + serviceCharge, tab switches
//   zero all three values.
// - Promotions come from the parent's usePromoEvaluator instance
//   (SellSurface owns it, mirroring the legacy page-level composition at
//   CashierSalesPage.js:215-230, so the Task 5 charge path sees the same
//   applied promo). The offer bar appears only when eligible promos exist
//   (legacy 2934-2949); the bottom sheet mirrors the legacy picker modal
//   (3019-3061) with the mockup's sheet styling.
// - Hold is offered only for dining options whose config requires a table
//   number — the exact condition legacy uses to route a submit into the
//   pending-order flow instead of an immediate sale (CashierSalesPage.js:
//   905-915: diningConfig != null && diningConfig !== ''). Charge / Hold
//   actions themselves are wired in Tasks 5–6 via the onCharge / onHold
//   props; editingOrderKey (Task 6) swaps the label to UPDATE ORDER
//   (legacy 2098-2105) and shows the editing chip (legacy 1839-1843).
//
// Task N round 3 (user-QC finding 1) — the promo scrim + bottom sheet are
// now rendered through a React portal to document.body instead of inline
// inside `.crt-root`. Root cause of the original bug: `.crt-root` sets its
// own `backdrop-filter` (the "glass" cart column), and per the CSS spec any
// element with a non-none `backdrop-filter` becomes the CONTAINING BLOCK for
// `position:fixed` descendants (same trap as `filter`/`transform`/
// `will-change`) — so `.crt-sheet`'s `position:fixed` was never actually
// relative to the viewport, it was relative to the 348px-wide, right-
// anchored cart column. `inset-inline-start:50%` centered the (much wider,
// `min(560px, 100vw-32px)`) sheet on THAT box, pushing most of it past the
// cart column's right edge — clipped by `.slf-root`'s `overflow:hidden`,
// which read as "half off-screen, bottom-right" in the iPad-portrait
// screenshot. The scrim had the identical bug (only covered the 348px
// column, not the full viewport). Portaling both to `document.body` — the
// one node in the tree with no filter/backdrop-filter/transform ancestor —
// restores true viewport-fixed positioning, matching the mockup's own flat
// DOM (`#scrim`/`#promoSheet` are top-level siblings, not nested inside any
// glass panel — docs/mockups/f12a-n/cashier-sell.html:834,914). The `<style>`
// tag's rules are global regardless of where the `<aside>` mounts, so no
// CSS changes were needed beyond adding safe-area bottom padding.
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import ManagerGate from '../../../components/pos/ManagerGate';
import { money, cartItemCount } from './lib/money';
import {
    useLanguage, getProductName, getTransliteration, getEntityTranslation, translateDynamic,
} from '../../../context/LanguageContext';

// Tokens only (--c-* theme vars, light + dark). Logical properties keep the
// column RTL-correct; money is tabular; steppers/remove keep ≥44px hit areas
// via ::after overlays on the mockup's compact 26-28px controls.
const CSS = `
.crt-root{flex:none;width:348px;min-height:0;display:flex;flex-direction:column;
  background:color-mix(in srgb, var(--c-surface) 78%, transparent);
  border-inline-start:1px solid color-mix(in srgb, var(--c-border) 55%, transparent);
  -webkit-backdrop-filter:blur(20px) saturate(1.5);backdrop-filter:blur(20px) saturate(1.5);}
/* Scoped away from sheet mode: this rule is what left the phone grid ~93px
   (F15). In the sheet the panel owns the full width instead. */
@media (max-width: 980px){.crt-root:not(.crt-root--sheet){width:300px;}}
/* F15 — inside SellSurface's phone sheet: fill it, and drop the column
   divider and glass, since the sheet provides its own surface and edge. */
.crt-root--sheet{flex:1;width:100%;min-height:0;background:transparent;
  border-inline-start:none;-webkit-backdrop-filter:none;backdrop-filter:none;}

/* header — mockup .cart-head */
.crt-head{flex:none;display:flex;align-items:center;gap:10px;padding:14px 16px 10px;
  border-bottom:1px solid color-mix(in srgb, var(--c-border) 55%, transparent);}
.crt-ctx{flex:1;min-width:0;}
.crt-l1{font-size:15px;font-weight:800;letter-spacing:-.2px;color:var(--c-ink);
  white-space:nowrap;overflow:hidden;text-overflow:ellipsis;font-variant-numeric:tabular-nums;}
.crt-l2{font-size:11.5px;color:var(--c-muted);font-weight:600;}
.crt-count{flex:none;font-size:11px;font-weight:800;color:var(--c-green);background:var(--c-greenLight);
  border:1px solid var(--c-greenBorder);border-radius:999px;padding:3px 10px;font-variant-numeric:tabular-nums;}
.crt-editing{flex:none;display:inline-flex;align-items:center;gap:5px;font-size:11px;font-weight:800;
  color:var(--c-amber);background:var(--c-amberLight);border:1px solid var(--c-amberBorder);
  border-radius:999px;padding:3px 10px;}

/* body — the ONLY scroller in the column */
.crt-body{flex:1;min-height:0;overflow-y:auto;padding:10px 14px;}

/* empty state — mockup .cart-empty */
.crt-empty{height:100%;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:12px;color:var(--c-muted);text-align:center;padding:20px;}
.crt-empty-ic{width:64px;height:64px;border-radius:20px;background:var(--c-faint);border:1px solid var(--c-border);display:flex;align-items:center;justify-content:center;color:var(--c-muted);}
.crt-empty-t1{font-size:14px;font-weight:700;color:var(--c-slate);}
.crt-empty-t2{font-size:12px;max-width:200px;font-weight:600;}

/* line items — mockup .line */
.crt-line{display:flex;gap:10px;padding:10px 0;align-items:flex-start;
  border-bottom:1px solid color-mix(in srgb, var(--c-border) 55%, transparent);}
.crt-line-main{flex:1;min-width:0;}
.crt-line-name{font-size:13.5px;font-weight:700;color:var(--c-ink);}
.crt-addon{display:inline-flex;align-items:center;gap:4px;margin-block-start:4px;margin-inline-end:4px;
  font-size:11px;font-weight:700;color:var(--c-green);background:var(--c-greenLight);
  border:1px solid var(--c-greenBorder);border-radius:999px;padding:2px 9px;font-variant-numeric:tabular-nums;}
.crt-addon-d1{margin-inline-start:14px;opacity:.85;}
.crt-addon-d2{margin-inline-start:28px;opacity:.75;}
.crt-note{display:block;width:100%;margin-block-start:4px;font-family:inherit;font-size:11.5px;font-style:italic;
  color:var(--c-muted);background:transparent;border:none;border-bottom:1px dashed var(--c-border);
  padding:2px 0;outline:none;}
.crt-note:focus{border-bottom-color:var(--c-green);color:var(--c-ink);}
.crt-note::placeholder{color:var(--c-mutedLight);opacity:.7;}
.crt-line-side{flex:none;display:flex;flex-direction:column;align-items:flex-end;gap:7px;}
.crt-line-total{font-size:13.5px;font-weight:800;color:var(--c-ink);font-variant-numeric:tabular-nums;}
.crt-stepper{display:inline-flex;align-items:center;gap:2px;background:var(--c-surface);
  border:1px solid var(--c-border);border-radius:999px;padding:2px;}
.crt-stepper button{position:relative;width:28px;height:28px;border-radius:50%;border:none;cursor:pointer;
  display:flex;align-items:center;justify-content:center;background:transparent;color:var(--c-slate);}
.crt-stepper button::after{content:"";position:absolute;inset:-8px;}
.crt-stepper button:hover{background:var(--c-greenLight);color:var(--c-green);}
.crt-qty{min-width:22px;text-align:center;font-size:13px;font-weight:800;color:var(--c-ink);font-variant-numeric:tabular-nums;}
.crt-line-x{position:relative;flex:none;width:26px;height:26px;margin-block-start:2px;border-radius:8px;border:none;cursor:pointer;
  display:flex;align-items:center;justify-content:center;background:transparent;color:var(--c-muted);}
.crt-line-x::after{content:"";position:absolute;inset:-9px;}
.crt-line-x:hover{color:var(--c-red);background:var(--c-redLight);}

/* offer bar — mockup .offer-bar */
.crt-offer{display:flex;align-items:center;gap:9px;width:100%;margin:10px 0 2px;padding:10px 13px;
  border-radius:12px;background:linear-gradient(90deg, var(--c-greenLight), transparent 130%);
  border:1px dashed var(--c-greenBorder);color:var(--c-green);font-family:inherit;
  font-size:12.5px;font-weight:700;text-align:start;cursor:pointer;min-height:44px;}
.crt-offer:hover{border-style:solid;}
.crt-offer .grow{flex:1;}

/* inline expanders — mockup .exp */
.crt-exp{border:1px solid color-mix(in srgb, var(--c-border) 75%, transparent);border-radius:12px;
  margin-block-start:8px;background:var(--c-surface);overflow:hidden;}
.crt-exp-head{display:flex;align-items:center;gap:9px;width:100%;min-height:44px;padding:10px 12px;border:none;cursor:pointer;
  background:transparent;font-family:inherit;font-size:12.5px;font-weight:700;color:var(--c-slate);text-align:start;}
.crt-exp-head .chev{margin-inline-start:auto;transition:transform .15s;color:var(--c-muted);}
.crt-exp.open .crt-exp-head .chev{transform:rotate(180deg);}
.crt-exp-badge{font-size:11px;font-weight:800;color:var(--c-green);font-variant-numeric:tabular-nums;}
.crt-exp-body{padding:0 12px 12px;}
.crt-dtabs{display:flex;gap:5px;margin-bottom:9px;}
.crt-dtab{flex:1;height:32px;border-radius:9px;font-family:inherit;font-size:11.5px;font-weight:700;cursor:pointer;
  background:var(--c-faint);border:1px solid var(--c-border);color:var(--c-muted);position:relative;}
.crt-dtab::after{content:"";position:absolute;inset-block:-6px;inset-inline:0;}
.crt-dtab.active{background:var(--c-greenLight);border-color:var(--c-greenBorder);color:var(--c-green);}
.crt-drow{display:flex;align-items:center;gap:8px;}
.crt-dunit{flex:none;font-size:12px;font-weight:700;color:var(--c-muted);}
.crt-dinput{flex:1;min-width:0;height:38px;border:1px solid var(--c-border);border-radius:10px;padding:0 12px;
  background:var(--c-faint);font-family:inherit;font-size:13px;color:var(--c-ink);outline:none;
  font-variant-numeric:tabular-nums;}
.crt-dinput:focus{border-color:var(--c-green);}
textarea.crt-dinput{height:64px;padding:9px 12px;resize:none;font-variant-numeric:normal;}
.crt-dclear{position:relative;flex:none;width:28px;height:28px;border-radius:8px;border:none;cursor:pointer;
  display:flex;align-items:center;justify-content:center;background:transparent;color:var(--c-red);}
.crt-dclear::after{content:"";position:absolute;inset:-8px;}
.crt-dclear:hover{background:var(--c-redLight);}
.crt-dapply{flex:none;min-height:34px;padding:0 14px;border-radius:9px;border:none;cursor:pointer;
  background:var(--c-green);color:#fff;font-family:inherit;font-size:12.5px;font-weight:800;
  letter-spacing:.2px;transition:background .15s ease, transform .12s ease;}
.crt-dapply:hover{background:var(--c-greenDark);}
.crt-dapply:active{transform:scale(.96);}

/* Clear-basket action row — mockup omitted it; legacy Cart action row's
   third button (CashierSalesPage.js:2013-2029), styled subtle-danger per
   tokens like every other destructive control in this surface. */
.crt-clear{position:relative;display:flex;align-items:center;justify-content:center;gap:7px;width:100%;
  min-height:40px;margin-block-start:8px;border-radius:12px;border:1px solid var(--c-redBorder);
  background:var(--c-redLight);color:var(--c-red);font-family:inherit;font-size:12.5px;font-weight:700;cursor:pointer;}
.crt-clear:hover:not(:disabled){background:var(--c-red);color:#fff;border-color:var(--c-red);}
.crt-clear:disabled{opacity:.4;cursor:not-allowed;}

/* totals — PINNED under the scroller (mockup .totals) */
.crt-totals{flex:none;padding:10px 16px 12px;border-top:1px solid color-mix(in srgb, var(--c-border) 55%, transparent);}
.crt-trow{display:flex;align-items:center;justify-content:space-between;padding:3px 0;
  font-size:12.5px;color:var(--c-slate);font-weight:600;}
.crt-trow .money{font-variant-numeric:tabular-nums;}
.crt-check{display:inline-flex;align-items:center;gap:7px;cursor:pointer;user-select:none;}
.crt-check input{width:16px;height:16px;accent-color:var(--c-green);cursor:pointer;}
.crt-trow.disc{color:var(--c-red);}
.crt-trow.markup{color:var(--c-green);}
.crt-trow.promo{color:var(--c-green);font-weight:700;}
.crt-promo-x{position:relative;width:20px;height:20px;margin-inline-start:6px;border-radius:6px;border:none;cursor:pointer;
  display:inline-flex;align-items:center;justify-content:center;background:transparent;color:var(--c-green);}
.crt-promo-x::after{content:"";position:absolute;inset:-12px;}
.crt-promo-x:hover{background:var(--c-greenLight);}
.crt-trow.grand{padding-top:8px;margin-top:5px;font-size:16.5px;font-weight:800;color:var(--c-ink);
  border-top:1px solid color-mix(in srgb, var(--c-border) 55%, transparent);}
.crt-charge{width:100%;height:54px;margin-top:10px;border-radius:14px;border:none;cursor:pointer;
  background:var(--c-green);color:#fff;font-family:inherit;font-size:16px;font-weight:800;letter-spacing:.2px;
  box-shadow:0 8px 20px color-mix(in srgb, var(--c-green) 38%, transparent);
  display:flex;align-items:center;justify-content:center;gap:10px;transition:transform .1s, background .12s;
  font-variant-numeric:tabular-nums;}
.crt-charge:hover:not(:disabled){background:var(--c-greenDark);}
.crt-charge:active:not(:disabled){transform:scale(.99);}
.crt-charge:disabled{opacity:.5;cursor:not-allowed;box-shadow:none;}
/* Charge + Hold side by side, equal weight, for a table/tent order. */
.crt-actions{display:flex;gap:8px;}
.crt-actions .crt-charge{flex:1;min-width:0;}
.crt-charge--alt{background:var(--c-surface);color:var(--c-green);
  border:1.5px solid var(--c-greenBorder);box-shadow:none;}
.crt-charge--alt:hover:not(:disabled){background:var(--c-greenLight);}
.crt-hold{width:100%;height:44px;margin-top:8px;border-radius:12px;cursor:pointer;
  border:1.5px solid var(--c-border);background:transparent;color:var(--c-slate);
  font-family:inherit;font-size:13.5px;font-weight:700;}
.crt-hold:hover{border-color:var(--c-greenBorder);color:var(--c-green);}

/* promo bottom sheet — mockup .sheet */
.crt-scrim{position:fixed;inset:0;z-index:40;background:rgba(9,13,22,.45);
  -webkit-backdrop-filter:blur(2px);backdrop-filter:blur(2px);}
.crt-sheet{position:fixed;z-index:50;inset-inline-start:50%;bottom:0;transform:translateX(-50%);
  width:min(560px, calc(100vw - 32px));max-height:70vh;display:flex;flex-direction:column;
  background:var(--c-surface);border:1px solid var(--c-border);border-bottom:none;
  border-radius:20px 20px 0 0;box-shadow:0 -12px 40px rgba(2,8,23,.25);
  padding:10px 18px calc(22px + var(--safe-bottom, 0px));}
.crt-grab{flex:none;width:40px;height:4px;border-radius:99px;background:var(--c-border);margin:2px auto 12px;}
.crt-sheet-head{flex:none;display:flex;align-items:center;gap:10px;padding-bottom:10px;}
.crt-sheet-title{flex:1;font-size:17px;font-weight:800;letter-spacing:-.2px;color:var(--c-ink);}
.crt-sheet-x{width:36px;height:36px;border-radius:10px;border:1px solid var(--c-border);cursor:pointer;
  display:flex;align-items:center;justify-content:center;color:var(--c-muted);background:var(--c-faint);}
.crt-sheet-x:hover{color:var(--c-red);background:var(--c-redLight);border-color:var(--c-redBorder);}
.crt-sheet-body{flex:1;min-height:0;overflow-y:auto;}
.crt-promo-row{display:flex;align-items:center;gap:12px;width:100%;padding:13px 14px;margin-bottom:8px;
  text-align:start;border:1.5px solid var(--c-border);border-radius:14px;background:var(--c-surface);
  font-family:inherit;cursor:pointer;min-height:44px;}
.crt-promo-row:hover{border-color:var(--c-green);}
.crt-promo-row.applied{border-color:var(--c-green);background:var(--c-greenLight);}
.crt-promo-ic{flex:none;width:40px;height:40px;border-radius:11px;background:var(--c-greenLight);
  border:1px solid var(--c-greenBorder);display:flex;align-items:center;justify-content:center;color:var(--c-green);}
.crt-promo-main{flex:1;min-width:0;}
.crt-promo-t{font-size:13.5px;font-weight:800;color:var(--c-ink);}
.crt-promo-d{font-size:11.5px;color:var(--c-muted);font-weight:600;}
.crt-promo-amt{flex:none;font-size:14px;font-weight:800;color:var(--c-green);font-variant-numeric:tabular-nums;}
.crt-promo-none{text-align:center;padding:20px 0;font-size:13px;font-weight:600;color:var(--c-muted);}
`;

const BasketIcon = (
    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="m5 11 4-7M19 11l-4-7" />
        <path d="M2 11h20l-1.8 8.2a2 2 0 0 1-2 1.8H5.8a2 2 0 0 1-2-1.8L2 11Z" />
        <path d="M9 15v3M15 15v3" />
    </svg>
);
const MinusIcon = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M5 12h14" /></svg>
);
const PlusIcon = (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true"><path d="M12 5v14M5 12h14" /></svg>
);
const XIcon = ({ size = 13 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true"><path d="M18 6 6 18M6 6l12 12" /></svg>
);
const SparkleIcon = (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9L12 3Z" />
        <path d="M19 15l.8 2.2L22 18l-2.2.8L19 21l-.8-2.2L16 18l2.2-.8L19 15Z" />
    </svg>
);
const DiscountIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="9" /><path d="m9 15 6-6" />
        <circle cx="9.5" cy="9.5" r="1" fill="currentColor" /><circle cx="14.5" cy="14.5" r="1" fill="currentColor" />
    </svg>
);
const NotesIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M4 4h16v12H9l-5 4V4Z" />
    </svg>
);
const ChevronIcon = (
    <svg className="chev" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
);
const EditIcon = (
    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M17 3a2.8 2.8 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3Z" />
    </svg>
);
const TrashIcon = (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2m3 0-1 14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2L4 6" />
    </svg>
);
const TagIcon = (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M7 7h.01M7 3h5c.5 0 1 .2 1.4.6l7 7a2 2 0 0 1 0 2.8l-7 7a2 2 0 0 1-2.8 0l-7-7A2 2 0 0 1 3 12V7a4 4 0 0 1 4-4Z" />
    </svg>
);

// Shared with SellSurface's F15 phone cart bar so the summary and the panel
// format money identically — see lib/money.js for why it lives there.


export default function CartPanel({
    builder,
    promo,
    currency,
    tenantConfig,
    selectedDiningOption,
    diningOptionObjects,
    availableTableNumbers,
    tableNumber,
    editingOrderKey = null,
    onCharge = () => {},
    onHold = () => {},
    onClearBasket = () => {},
    chargeDisabled = false,
    // F15: true when hosted in SellSurface's phone cart sheet rather than the
    // tablet stage column. Layout only — no behaviour differs between them.
    inSheet = false,
    // F15 part 2 — 'cashier' (default, unchanged) or 'waiter'.
    //
    // The Waiter Sales surface reuses this panel wholesale rather than growing
    // a second cart: same builder state, same line rows, same steppers, same
    // notes. Only two things differ, and both follow from waiters never
    // handling money — the primary action SENDS to the kitchen instead of
    // charging, and there is no Discount expander (drawerFlow's calculateTotal
    // has no discount term at all, so offering one here would let a waiter
    // enter a discount that is silently dropped on submit). Hold is also
    // suppressed: "hold" is the cashier's pending-order flow, and a waiter
    // order IS a pending order — the two would be the same button twice.
    mode = 'cashier',
}) {
    const isWaiterMode = mode === 'waiter';
    const { t, language, translationDetails } = useLanguage();
    const {
        selectedProducts,
        notes, setNotes,
        addServiceCharge, setAddServiceCharge,
        addTax,
        discountAmount, setDiscountAmount,
        discountMode, setDiscountMode,
        discountPercentage, setDiscountPercentage,
        payableAmount, setPayableAmount,
        showDiscountPanel, setShowDiscountPanel,
        showNotesPanel, setShowNotesPanel,
        memoizedTotals,
        handleCalculateTotals,
        handleUpdateProductQuantity,
        handleRemoveProduct,
        handleUpdateProductNotes,
    } = builder;

    const {
        appliedPromo, allEligibleOptions, showPromoModal, setShowPromoModal,
        selectPromo, removePromo, promoDiscountAmount,
    } = promo;

    // Uncommitted discount entry — see requestDiscountApply below. `draft` is
    // what the cashier has typed; `pendingDiscount` is the resolved amount
    // waiting on a manager. Neither affects the totals.
    const [draft, setDraft] = useState('');
    const [pendingDiscount, setPendingDiscount] = useState(null);

    // Escape dismisses this column's own overlays, highest-open-layer-first
    // (promo sheet > discount expander > notes expander — Task 8 item 9:
    // the discount/notes expanders never had Escape handling before, and
    // this column is the sole owner of all three, so centralizing them
    // HERE — rather than SellSurface, which cannot see this local state
    // without lifting it — keeps exactly one keypress closing exactly one
    // layer even when more than one is open at once).
    useEffect(() => {
        const openLayer = showPromoModal ? 'promo' : showDiscountPanel ? 'discount' : showNotesPanel ? 'notes' : null;
        if (!openLayer) return undefined;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            if (openLayer === 'promo') setShowPromoModal(false);
            else if (openLayer === 'discount') setShowDiscountPanel(false);
            else setShowNotesPanel(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [showPromoModal, showDiscountPanel, showNotesPanel, setShowPromoModal, setShowDiscountPanel, setShowNotesPanel]);

    const hasItems = selectedProducts.length > 0;
    // Shared with SellSurface's phone cart bar (lib/money.js) so the bar and
    // this badge always show the same number.
    const itemCount = cartItemCount(selectedProducts);

    // Header context — legacy 1828-1835 (option label · table/counter).
    const optionLabel = selectedDiningOption
        ? (getEntityTranslation(diningOptionObjects?.[selectedDiningOption], language, translationDetails)
            || translateDynamic(selectedDiningOption, t))
        : null;
    const headline = optionLabel
        ? `${optionLabel}${tableNumber ? ` · ${tableNumber}` : ''}`
        : t('order');

    // Hold is only meaningful for options whose submit routes to the
    // pending-order flow — the exact legacy condition (905-915).
    const diningConfig = availableTableNumbers?.[selectedDiningOption];
    const requiresTableNumber = diningConfig != null && diningConfig !== '';
    // Table/tent orders offer BOTH Charge and Hold; everything else is a
    // straight immediate sale. Editing keeps UPDATE ORDER as the single
    // action (legacy 2090 semantics) — re-holding an order being edited is
    // not a thing.
    const showBothActions = !isWaiterMode && requiresTableNumber && !editingOrderKey;
    // Subheading mirrors the mockup: daily-counter hint for no-table options.
    const subline = !selectedDiningOption
        ? t('select_dining_option_to_begin')
        : (requiresTableNumber
            ? t('table_order')
            : t('daily_counter_note', {}, 'Daily counter · resets each business day'));

    // Totals — calculateTotals output via the builder (NEVER reimplemented);
    // display total nets off the applied promo exactly like legacy 2043.
    const totals = memoizedTotals || handleCalculateTotals();
    const displayTotal = Math.max(0, totals.total - (promoDiscountAmount || 0));
    const svcPercent = Math.round((tenantConfig?.service_charge_rate || 0.12) * 100);
    // Independent of service_charge_applied: 'applies + locked' is the
    // combination the client asked for and one flag cannot express it.
    const serviceChargeToggleVisible =
        diningOptionObjects?.[selectedDiningOption]?.service_charge_toggle_visible !== false;
    const taxPercent = Math.round((tenantConfig?.tax_rate || 0) * 100);

    const productKey = (p) => p.uniqueId || `${p.product_code}-${p.notes || ''}`;
    const productId = (p) => p.uniqueId || p.product_code;
    const lineTotal = (p) => {
        const qty = parseInt(p.quantity) || 1;
        const addons = (p.add_ons || []).reduce((sum, a) => sum + (parseFloat(a.price) || 0), 0);
        return ((parseFloat(p.selling_price) || 0) + addons) * qty;
    };
    const lineName = (p) =>
        getProductName(p, language, translationDetails) ?? getTransliteration(p.name, language, t);

    // Discount tab switch zeroes every discount value (legacy 1924).
    const switchDiscountMode = (mode) => {
        setDiscountMode(mode);
        setDiscountAmount(0);
        setDiscountPercentage(0);
        setPayableAmount(0);
        setDraft('');
        setPendingDiscount(null);
    };
    const clearDiscount = () => {
        setDiscountAmount(0);
        setDiscountPercentage(0);
        setPayableAmount(0);
        setDraft('');
    };

    // ---- manager-gated discount -----------------------------------------
    // Client request 2026-08-26: a discount must be approved by a manager
    // before it reaches the cart. The typed value therefore lives in LOCAL
    // draft state and only moves into the builder (which drives the totals)
    // once /manager-approvals/verify has confirmed a manager. Typing is free;
    // applying is not. Clearing a discount is never gated — it takes money
    // off nobody.
    const requestDiscountApply = () => {
        const { subtotal, serviceCharge } = handleCalculateTotals();
        const typed = parseFloat(draft) || 0;
        let amount = 0;
        if (discountMode === 'direct') amount = typed;
        else if (discountMode === 'percentage') amount = parseFloat((subtotal * typed / 100).toFixed(2));
        else amount = parseFloat((subtotal + serviceCharge - typed).toFixed(2));
        if (!amount) return;
        setPendingDiscount({ amount, typed });
    };

    const onDiscountApproved = () => {
        if (!pendingDiscount) return;
        setDiscountAmount(pendingDiscount.amount);
        if (discountMode === 'percentage') setDiscountPercentage(pendingDiscount.typed);
        if (discountMode === 'payable') setPayableAmount(pendingDiscount.typed);
        setPendingDiscount(null);
    };

    const chargeLabel = editingOrderKey
        ? t('update_order')
        : isWaiterMode ? t('send_to_kitchen') : t('charge');

    return (
        <aside className={inSheet ? 'crt-root crt-root--sheet' : 'crt-root'} aria-label={t('order')}>
            <style>{CSS}</style>

            {/* Header */}
            <div className="crt-head">
                <div className="crt-ctx">
                    <div className="crt-l1">{headline}</div>
                    <div className="crt-l2">{subline}</div>
                </div>
                {editingOrderKey ? (
                    <span className="crt-editing" data-testid="editing-chip">{EditIcon}{t('editing_order')}</span>
                ) : (
                    hasItems && <span className="crt-count">{itemCount} {t('items')}</span>
                )}
            </div>

            {/* Body — the only scroller */}
            <div className="crt-body">
                {!hasItems ? (
                    <div className="crt-empty">
                        <div className="crt-empty-ic">{BasketIcon}</div>
                        <div className="crt-empty-t1">{t('no_products_selected')}</div>
                        {!selectedDiningOption && (
                            <div className="crt-empty-t2">{t('select_dining_option_to_begin')}</div>
                        )}
                    </div>
                ) : (
                    <>
                        {selectedProducts.map((product) => (
                            <div className="crt-line" key={productKey(product)}>
                                <div className="crt-line-main">
                                    <div className="crt-line-name">{lineName(product)}</div>
                                    {(product.add_ons || []).map((addon, idx) => (
                                        <span
                                            className={`crt-addon${addon.depth ? ` crt-addon-d${Math.min(addon.depth, 2)}` : ''}`}
                                            key={`${addon.parent_code || ''}-${addon.product_code || addon.id || idx}`}
                                        >
                                            {addon.depth ? '↳' : '+'} {addon.name}{' '}
                                            {addon.price ? <span className="money">{money(currency, addon.price)}</span> : null}
                                        </span>
                                    ))}
                                    <input
                                        type="text"
                                        className="crt-note"
                                        placeholder={t('add_notes_for_item')}
                                        aria-label={t('add_notes_for_item')}
                                        value={product.notes || ''}
                                        onChange={(e) => handleUpdateProductNotes(productId(product), e.target.value)}
                                    />
                                </div>
                                <div className="crt-line-side">
                                    <span className="crt-line-total">{money(currency, lineTotal(product))}</span>
                                    <span className="crt-stepper">
                                        <button
                                            type="button"
                                            aria-label={t('decrease_quantity', {}, 'Decrease quantity')}
                                            onClick={() => handleUpdateProductQuantity(productId(product), Math.max(1, product.quantity - 1))}
                                        >{MinusIcon}</button>
                                        <span className="crt-qty">{product.quantity}</span>
                                        <button
                                            type="button"
                                            aria-label={t('increase_quantity', {}, 'Increase quantity')}
                                            onClick={() => handleUpdateProductQuantity(productId(product), product.quantity + 1)}
                                        >{PlusIcon}</button>
                                    </span>
                                </div>
                                <button
                                    type="button"
                                    className="crt-line-x"
                                    aria-label={t('remove')}
                                    onClick={() => handleRemoveProduct(productId(product))}
                                ><XIcon /></button>
                            </div>
                        ))}

                        {/* Offer bar — only when eligible promos exist (legacy 2934-2949) */}
                        {allEligibleOptions.length > 0 && (
                            <button type="button" className="crt-offer" onClick={() => setShowPromoModal(true)}>
                                {SparkleIcon}
                                <span className="grow">
                                    {t('n_offers_available', { count: allEligibleOptions.length }, `${allEligibleOptions.length} offers available`)}
                                </span>
                                <span>{t('apply_offer')}</span>
                            </button>
                        )}

                        {/* Discount expander — legacy 1912-1979 semantics.
                            Not offered in waiter mode: drawerFlow's
                            calculateTotal has no discount term, so anything
                            entered here would be silently dropped on submit —
                            worse than not offering it. */}
                        {!isWaiterMode && (
                        <div className={`crt-exp${showDiscountPanel ? ' open' : ''}`}>
                            <button
                                type="button"
                                className="crt-exp-head"
                                aria-expanded={showDiscountPanel}
                                onClick={() => { setShowDiscountPanel(!showDiscountPanel); setShowNotesPanel(false); }}
                            >
                                {DiscountIcon}
                                {t('discount')}
                                {discountAmount !== 0 && (
                                    <span className="crt-exp-badge">
                                        {discountAmount > 0 ? '−' : '+'}{money(currency, Math.abs(parseFloat(discountAmount)))}
                                    </span>
                                )}
                                {ChevronIcon}
                            </button>
                            {showDiscountPanel && (
                                <div className="crt-exp-body">
                                    <div className="crt-dtabs" role="tablist">
                                        {[
                                            { key: 'direct', label: t('fixed_discount') },
                                            { key: 'percentage', label: t('percentage') },
                                            { key: 'payable', label: t('payable_amount') },
                                        ].map((opt) => (
                                            <button
                                                key={opt.key}
                                                type="button"
                                                role="tab"
                                                aria-selected={discountMode === opt.key}
                                                className={`crt-dtab${discountMode === opt.key ? ' active' : ''}`}
                                                onClick={() => switchDiscountMode(opt.key)}
                                            >{opt.label}</button>
                                        ))}
                                    </div>
                                    <div className="crt-drow">
                                        {/* All three inputs write to the local
                                            `draft` only — nothing reaches the
                                            builder (and so the totals) until a
                                            manager approves via Apply. */}
                                        {discountMode === 'direct' && (<>
                                            <span className="crt-dunit">{currency}</span>
                                            <input
                                                type="number" min="0" step="0.01" placeholder="0.00"
                                                className="crt-dinput" data-testid="discount-input"
                                                aria-label={t('discount')}
                                                value={draft}
                                                onChange={(e) => setDraft(e.target.value)}
                                                autoFocus
                                            />
                                        </>)}
                                        {discountMode === 'percentage' && (<>
                                            <input
                                                type="number" min="0" max="100" step="1" placeholder="0"
                                                className="crt-dinput" data-testid="discount-input"
                                                aria-label={t('percentage')}
                                                value={draft}
                                                onChange={(e) => setDraft(e.target.value)}
                                                autoFocus
                                            />
                                            <span className="crt-dunit">%</span>
                                        </>)}
                                        {discountMode === 'payable' && (<>
                                            <span className="crt-dunit">{currency}</span>
                                            <input
                                                type="number" step="0.01" placeholder="0.00"
                                                className="crt-dinput" data-testid="discount-input"
                                                aria-label={t('payable_amount')}
                                                value={draft}
                                                onChange={(e) => setDraft(e.target.value)}
                                                autoFocus
                                            />
                                        </>)}
                                        <button
                                            type="button"
                                            className="crt-dapply"
                                            data-testid="discount-apply"
                                            onClick={requestDiscountApply}
                                        >
                                            {t('apply')}
                                        </button>
                                        {discountAmount !== 0 && (
                                            <button type="button" className="crt-dclear" aria-label={t('remove')} onClick={clearDiscount}>
                                                <XIcon />
                                            </button>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                        )}

                        {/* Notes expander — order-level notes (legacy 1981-1994) */}
                        <div className={`crt-exp${showNotesPanel ? ' open' : ''}`}>
                            <button
                                type="button"
                                className="crt-exp-head"
                                aria-expanded={showNotesPanel}
                                onClick={() => { setShowNotesPanel(!showNotesPanel); setShowDiscountPanel(false); }}
                            >
                                {NotesIcon}
                                {t('order_notes')}
                                {notes && <span className="crt-exp-badge">•</span>}
                                {ChevronIcon}
                            </button>
                            {showNotesPanel && (
                                <div className="crt-exp-body">
                                    <textarea
                                        className="crt-dinput"
                                        placeholder={t('add_order_notes')}
                                        aria-label={t('add_order_notes')}
                                        value={notes}
                                        onChange={(e) => setNotes(e.target.value)}
                                        autoFocus
                                    />
                                </div>
                            )}
                        </div>
                    </>
                )}

                {/* Clear basket — legacy Cart action row's third button
                    (2013-2029). Rendered regardless of hasItems (unlike the
                    Discount/Notes expanders, which are cart-content
                    affordances) so its disabled condition — nothing to
                    clear: no items, no notes, no discount — matches legacy
                    exactly rather than being permanently satisfied by an
                    hasItems gate. */}
                <button
                    type="button"
                    className="crt-clear"
                    data-testid="clear-basket-btn"
                    disabled={selectedProducts.length === 0 && !notes && !discountAmount}
                    onClick={onClearBasket}
                >
                    {TrashIcon}
                    {t('clear_basket')}
                </button>
            </div>

            {/* Totals + Charge/Hold — PINNED at the bottom */}
            {hasItems && (
                <div className="crt-totals" data-testid="totals-block">
                    <div className="crt-trow">
                        <span>{t('subtotal')}</span>
                        <span className="money">{money(currency, totals.subtotal)}</span>
                    </div>
                    {/* The service-charge LINE always shows — the customer
                        must be able to see what they are paying for. Only the
                        CONTROL is conditional: a dining option can apply the
                        charge automatically and forbid staff removing it
                        (client request 2026-08-26). Defaults to visible, so a
                        tenant who has not set the flag keeps today's control. */}
                    <div className="crt-trow">
                        {serviceChargeToggleVisible ? (
                            <label className="crt-check">
                                <input
                                    type="checkbox"
                                    data-testid="svc-check"
                                    checked={addServiceCharge}
                                    onChange={(e) => setAddServiceCharge(e.target.checked)}
                                />
                                {t('service_charge_with_percent', { percent: svcPercent })}
                            </label>
                        ) : (
                            <span>{t('service_charge_with_percent', { percent: svcPercent })}</span>
                        )}
                        <span className="money">{money(currency, totals.serviceCharge)}</span>
                    </div>
                    {addTax && totals.tax > 0 && (
                        <div className="crt-trow">
                            <span>{t('tax_with_percent', { percent: taxPercent })}</span>
                            <span className="money">{money(currency, totals.tax)}</span>
                        </div>
                    )}
                    {discountAmount !== 0 && (
                        <div className={`crt-trow ${discountAmount > 0 ? 'disc' : 'markup'}`}>
                            <span>{discountAmount > 0 ? t('discount') : t('markup', {}, 'Markup')}</span>
                            <span className="money">
                                {discountAmount > 0 ? '−' : '+'} {money(currency, Math.abs(parseFloat(discountAmount)))}
                            </span>
                        </div>
                    )}
                    {appliedPromo && (
                        <div className="crt-trow promo" data-testid="promo-line">
                            <span>{appliedPromo.promotion.name}</span>
                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                <span className="money">−{money(currency, promoDiscountAmount)}</span>
                                <button type="button" className="crt-promo-x" aria-label={t('remove_promo')} onClick={removePromo}>
                                    <XIcon size={12} />
                                </button>
                            </span>
                        </div>
                    )}
                    <div className="crt-trow grand">
                        <span>{t('total')}</span>
                        <span className="money">{money(currency, displayTotal)}</span>
                    </div>
                    {/* Both actions, equal weight, for a table/tent order
                        (client request 2026-08-26). Charge = send to the
                        kitchen, take payment and close in one tap — the
                        counter-service case with no waiter. Hold = the
                        pending-order flow, kept for events where a waiter
                        takes the order and it is settled later.

                        Charge used to be hidden here because
                        SellSurface.handleCharge always errored ('hold
                        first'); that guard was an unimplemented stub, not a
                        decision. While EDITING a held order the primary
                        action is UPDATE ORDER and Hold is not offered —
                        unchanged (legacy 2090 semantics). */}
                    <div className={showBothActions ? 'crt-actions' : undefined}>
                        <button
                            type="button"
                            className="crt-charge"
                            data-testid="charge-btn"
                            onClick={onCharge}
                            disabled={chargeDisabled}
                            aria-busy={chargeDisabled}
                        >
                            {chargeLabel} <span className="money">{money(currency, displayTotal)}</span>
                        </button>
                        {showBothActions && (
                            <button
                                type="button"
                                className="crt-charge crt-charge--alt"
                                data-testid="hold-btn"
                                onClick={onHold}
                            >
                                {t('hold_order', {}, 'Hold order')}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {/* Promo picker bottom sheet (mockup .sheet; legacy picker 3019-3061).
                Task N round 3 finding 1 — portaled to document.body (see the
                header comment above `createPortal`'s import) so `position:
                fixed` resolves against the real viewport instead of the
                backdrop-filter cart column. */}
            {showPromoModal && createPortal(
                <>
                    <div className="crt-scrim" data-testid="promo-scrim" onClick={() => setShowPromoModal(false)} />
                    <div className="crt-sheet" role="dialog" aria-modal="true" aria-label={t('select_offer')}>
                        <div className="crt-grab" />
                        <div className="crt-sheet-head">
                            <div className="crt-sheet-title">{t('select_offer')}</div>
                            <button type="button" className="crt-sheet-x" aria-label={t('close')} onClick={() => setShowPromoModal(false)}>
                                <XIcon size={16} />
                            </button>
                        </div>
                        <div className="crt-sheet-body">
                            {allEligibleOptions.length === 0 ? (
                                <p className="crt-promo-none">{t('no_offers_available')}</p>
                            ) : (
                                allEligibleOptions.map(({ promotion, discount }) => (
                                    <button
                                        key={promotion.promotion_id}
                                        type="button"
                                        className={`crt-promo-row${appliedPromo?.promotion?.promotion_id === promotion.promotion_id ? ' applied' : ''}`}
                                        onClick={() => selectPromo({ promotion, discount })}
                                    >
                                        <span className="crt-promo-ic">{TagIcon}</span>
                                        <span className="crt-promo-main">
                                            <span className="crt-promo-t">{promotion.name}</span>
                                            {promotion.description && (
                                                <><br /><span className="crt-promo-d">{promotion.description}</span></>
                                            )}
                                        </span>
                                        <span className="crt-promo-amt">−{money(currency, discount)}</span>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </>,
                document.body
            )}

            {/* Manager approval for the pending discount (client request
                2026-08-26). Rendered last so it layers above the expanders. */}
            <ManagerGate
                open={pendingDiscount != null}
                action="discount"
                context={pendingDiscount
                    ? { amount: pendingDiscount.amount, mode: discountMode }
                    : null}
                onApprove={onDiscountApproved}
                onCancel={() => setPendingDiscount(null)}
            />
        </aside>
    );
}
